import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { nip19 } from 'nostr-tools';
import {
  Check,
  Copy,
  ExternalLink,
  Flag,
  Hash,
  Pencil,
  Trash2,
} from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Layout } from '@/components/Layout';
import { VideoPlayer } from '@/components/VideoPlayer';
import { VideoReactions } from '@/components/VideoReactions';
import { VideoComments } from '@/components/VideoComments';
import { ReportDialog } from '@/components/ReportDialog';
import { RelatedVideos } from '@/components/RelatedVideos';
import { useAuthor } from '@/hooks/useAuthor';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useToast } from '@/hooks/useToast';
import { useDeleteVideo } from '@/hooks/useVideoActions';
import { genUserName } from '@/lib/genUserName';
import {
  formatDuration,
  getNIPVersionLabel,
  getVideoNip19,
  isVideoEditable,
  timeAgo,
  type VideoData,
} from '@/lib/videoTypes';

function useCopy() {
  const [copied, setCopied] = useState<string | null>(null);
  const { toast } = useToast();

  const copy = async (label: string, value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(label);
      setTimeout(() => setCopied(null), 1500);
    } catch {
      toast({ title: 'Failed to copy', variant: 'destructive' });
    }
  };

  return { copied, copy };
}

function CopyChip({ label, value }: { label: string; value: string }) {
  const { copied, copy } = useCopy();
  return (
    <Button
      variant="secondary"
      size="sm"
      className="gap-1.5 rounded-full text-xs"
      onClick={() => copy(label, value)}
      title={`Copy ${label}`}
    >
      {copied === label ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
      {label}
    </Button>
  );
}

function AuthorRow({ video }: { video: VideoData }) {
  const author = useAuthor(video.pubkey);
  const metadata = author.data?.metadata;
  const name = metadata?.name ?? genUserName(video.pubkey);
  const npub = nip19.npubEncode(video.pubkey);

  return (
    <div className="flex items-center gap-3 min-w-0">
      <Link to={`/${npub}`} className="shrink-0">
        <Avatar className="h-10 w-10">
          <AvatarImage src={metadata?.picture} alt={name} />
          <AvatarFallback>{name.slice(0, 2).toUpperCase()}</AvatarFallback>
        </Avatar>
      </Link>
      <div className="min-w-0">
        <Link to={`/${npub}`} className="block truncate font-semibold hover:text-primary transition-colors">
          {name}
        </Link>
        {metadata?.nip05 && (
          <span className="block truncate text-xs text-muted-foreground">{metadata.nip05}</span>
        )}
      </div>
    </div>
  );
}

export default function VideoPage({ video }: { video: VideoData }) {
  const { user } = useCurrentUser();
  const isOwn = user?.pubkey === video.pubkey;
  const navigate = useNavigate();
  const deleteVideo = useDeleteVideo();

  const note = nip19.noteEncode(video.id);
  const nip19Id = getVideoNip19(video);

  return (
    <Layout>
      <div className="mx-auto max-w-6xl px-3 sm:px-6 py-5">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-[1fr_340px]">
          <div className="min-w-0 space-y-4">
            <VideoPlayer video={video} />

            <div className="space-y-3">
              <h1 className="text-xl font-bold leading-snug">{video.title}</h1>

              <div className="flex flex-wrap items-center gap-x-3 gap-y-2 text-sm text-muted-foreground">
                <span>{timeAgo(video.createdAt)}</span>
                {video.duration !== undefined && (
                  <>
                    <span aria-hidden>·</span>
                    <span>{formatDuration(video.duration)}</span>
                  </>
                )}
                <span aria-hidden>·</span>
                <Badge variant="secondary" className="font-normal" title={getNIPVersionLabel(video.nipVersion)}>
                  {video.nipVersion === 'nip71-regular'
                    ? 'Regular'
                    : video.nipVersion === 'nip71-addressable'
                      ? 'Addressable'
                      : video.nipVersion === 'note-video'
                        ? 'Note (kind 1)'
                        : 'Legacy'}
                </Badge>
                {video.contentWarning && (
                  <Badge variant="outline" className="border-yellow-600/50 text-yellow-600 dark:text-yellow-500 font-normal">
                    CW: {video.contentWarning}
                  </Badge>
                )}
              </div>

              <div className="flex flex-wrap items-center justify-between gap-3">
                <AuthorRow video={video} />
                <div className="flex items-center gap-1.5">
                  {isOwn && isVideoEditable(video) && (
                    <Button
                      variant="secondary"
                      size="sm"
                      className="gap-1.5"
                      onClick={() => navigate(`/edit/${nip19Id}`)}
                    >
                      <Pencil className="h-3.5 w-3.5" /> Edit
                    </Button>
                  )}
                  {isOwn && (
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="secondary" size="sm" className="gap-1.5 text-destructive hover:text-destructive">
                          <Trash2 className="h-3.5 w-3.5" /> Delete
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete this video?</AlertDialogTitle>
                          <AlertDialogDescription>
                            This publishes a NIP-09 deletion request (kind 5) to your
                            relays. Relays that honor deletion requests will remove
                            the event, but copies may persist on relays that don't.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={() => {
                              deleteVideo.mutate(video, {
                                onSuccess: () => navigate('/'),
                              });
                            }}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                          >
                            Delete
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  )}
                  <ReportDialog video={video} />
                </div>
              </div>

              <VideoReactions video={video} />

              {/* Video URL + identifiers */}
              <Card>
                <CardContent className="space-y-3 p-4">
                  <div className="space-y-1">
                    <span className="text-xs font-medium text-muted-foreground">Video URL</span>
                    <a
                      href={video.videoUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1.5 break-all text-sm text-primary hover:underline"
                    >
                      <ExternalLink className="h-3.5 w-3.5 shrink-0" />
                      {video.videoUrl}
                    </a>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <CopyChip label="note" value={note} />
                    <CopyChip
                      label={video.dTag ? 'naddr' : 'nevent'}
                      value={nip19Id}
                    />
                    <span className="text-xs text-muted-foreground">
                      {getNIPVersionLabel(video.nipVersion)}
                    </span>
                  </div>
                </CardContent>
              </Card>

              {/* Description + hashtags */}
              {(video.description || video.hashtags.length > 0) && (
                <Card>
                  <CardContent className="space-y-3 p-4">
                    {video.hashtags.length > 0 && (
                      <div className="flex flex-wrap gap-1.5">
                        {video.hashtags.map((tag) => (
                          <Link
                            key={tag}
                            to={`/tag/${encodeURIComponent(tag)}`}
                            className="inline-flex items-center gap-0.5 rounded-full bg-secondary px-2.5 py-1 text-xs font-medium hover:bg-accent transition-colors"
                          >
                            <Hash className="h-3 w-3" />
                            {tag}
                          </Link>
                        ))}
                      </div>
                    )}
                    {video.description && (
                      <p className="whitespace-pre-wrap break-words text-sm leading-relaxed">
                        {video.description}
                      </p>
                    )}
                  </CardContent>
                </Card>
              )}
            </div>

            <VideoComments video={video} />
          </div>

          {/* Related videos sidebar */}
          <aside className="min-w-0">
            <RelatedVideos currentVideo={video} />
          </aside>
        </div>
      </div>
    </Layout>
  );
}

export function VideoPageSkeleton() {
  return (
    <Layout>
      <div className="mx-auto max-w-6xl px-3 sm:px-6 py-5">
        <div className="max-w-4xl space-y-4">
          <Skeleton className="aspect-video w-full rounded-xl" />
          <Skeleton className="h-6 w-3/4" />
          <div className="flex items-center gap-3">
            <Skeleton className="h-10 w-10 rounded-full" />
            <Skeleton className="h-4 w-40" />
          </div>
          <Skeleton className="h-24 w-full rounded-xl" />
        </div>
      </div>
    </Layout>
  );
}

export function VideoNotFound() {
  return (
    <Layout>
      <div className="mx-auto max-w-6xl px-3 sm:px-6 py-16">
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center gap-3 py-12 px-8 text-center">
            <Flag className="h-8 w-8 text-muted-foreground" />
            <p className="font-medium">Video not found</p>
            <p className="max-w-sm text-sm text-muted-foreground">
              This event could not be found on your relays, or it isn't a valid
              NIP-71 video event.
            </p>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
